// src/pages/HomePage.jsx
import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "../components/Sidebar";

const API = process.env.REACT_APP_API_URL;

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [email, setEmail] = useState("");
  const [orgId, setOrgId] = useState(null);
  const [role, setRole] = useState("");
  const [residencyCity, setResidencyCity] = useState("");

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd1, setNewPwd1] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdSavedMsg, setPwdSavedMsg] = useState("");
  const [pwdErr, setPwdErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const res = await fetch(`${API}/me`, { credentials: "include" });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (cancelled) return;
        setEmail(data.email || "");
        setOrgId(data.organization_id ?? null);
        setRole(data.role || "");
        setResidencyCity(data.residency_city || "");
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Fehler beim Laden der Einstellungen");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const passwordsMismatch = useMemo(
    () => newPwd1.length > 0 || newPwd2.length > 0 ? newPwd1 !== newPwd2 : false,
    [newPwd1, newPwd2]
  );

  async function savePassword(e) {
    e.preventDefault();
    setSavingPwd(true);
    setPwdSavedMsg("");
    setPwdErr("");

    if (newPwd1 !== newPwd2) {
      setPwdErr("Die neuen Passwörter stimmen nicht überein.");
      setSavingPwd(false);
      return;
    }

    try {
      const res = await fetch(`${API}/me/password`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: currentPwd,
          new_password: newPwd1,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();
      setPwdSavedMsg("Passwort aktualisiert.");
      setCurrentPwd("");
      setNewPwd1("");
      setNewPwd2("");
      setTimeout(() => setPwdSavedMsg(""), 2000);
    } catch (e) {
      setPwdErr(e?.message || "Passwortänderung fehlgeschlagen.");
    } finally {
      setSavingPwd(false);
    }
  }

  return (
    <div className="flex min-h-screen bg-[#cdeaff]">
      <Sidebar active="settings" />
      <main className="flex-1 px-0 pt-6 pb-0">
        <div className="w-full h-full rounded-tl-xl border border-black/10 bg-white/60 p-6 backdrop-blur">
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-3xl font-semibold text-gray-900">Einstellungen</h1>
          </div>

          {loading && <div className="text-sm text-gray-700">Lade Einstellungen…</div>}
          {err && !loading && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              {err}
            </div>
          )}

          {!loading && !err && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Profile Info */}
              <section className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold text-gray-900">Profil</h2>

                <div className="mb-4">
                  <label className="text-sm text-gray-600">E-Mail</label>
                  <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-800">
                    {email || "—"}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-sm text-gray-600">Organisation</label>
                  <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-800">
                    {orgId ?? "—"}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-sm text-gray-600">Rolle</label>
                  <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-800">
                    {role || "—"}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="text-sm text-gray-600">Residenz</label>
                  <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-800">
                    {residencyCity || "—"}
                  </div>
                </div>
              </section>

              {/* Password change */}
              <section className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold text-gray-900">Passwort ändern</h2>

                {pwdErr && (
                  <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                    {pwdErr}
                  </div>
                )}

                <form onSubmit={savePassword} className="space-y-4">
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      Aktuelles Passwort
                    </label>
                    <input
                      type="password"
                      value={currentPwd}
                      onChange={(e) => setCurrentPwd(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-blue-400 focus:outline-none"
                      autoComplete="current-password"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      Neues Passwort
                    </label>
                    <input
                      type="password"
                      value={newPwd1}
                      onChange={(e) => setNewPwd1(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-blue-400 focus:outline-none"
                      autoComplete="new-password"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      Neues Passwort bestätigen
                    </label>
                    <input
                      type="password"
                      value={newPwd2}
                      onChange={(e) => setNewPwd2(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-blue-400 focus:outline-none"
                      autoComplete="new-password"
                    />
                    {passwordsMismatch && (
                      <p className="mt-1 text-xs text-red-600">
                        Die neuen Passwörter stimmen nicht überein.
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <button
                      type="submit"
                      disabled={savingPwd}
                      className="inline-flex items-center rounded-lg bg-[#76B3FF] px-4 py-2 text-white font-semibold shadow hover:bg-[#5d9ef7] disabled:opacity-60"
                    >
                      {savingPwd ? "Aktualisiere…" : "Passwort speichern"}
                    </button>
                    {pwdSavedMsg && (
                      <span className="text-sm text-green-700">{pwdSavedMsg}</span>
                    )}
                  </div>
                </form>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
